/*
 * Bobby Rubber Stamp — stamp-hero interactions.
 *
 * 1. Spotlight + push-in: hovering a product hotspot dims the still-life
 *    (.is-hovering) and scales the whole stage in toward that product
 *    (.is-zooming + --zoom-x/--zoom-y). The scale is dropped under
 *    prefers-reduced-motion in CSS, so the spotlight still works there. The
 *    glow itself stays pure CSS.
 * 2. The category label is a single shared floating element positioned every
 *    frame from the active hotspot's live bounding box (see the CSS comment
 *    on .stamp-hero__float-label for why it isn't per-hotspot CSS anymore).
 * 3. A gentle GSAP load-in for the image + editorial text (no-ops without GSAP
 *    or under reduced motion).
 *
 * Idempotent — safe to re-run on shopify:section:load.
 */
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function gsapOk() {
    return typeof window.gsap !== 'undefined' && !reduce;
  }

  function initHover(root) {
    var active = null;
    var floatLabel = root.querySelector('[data-float-label]');
    var stage = root.querySelector('.stamp-hero__stage');
    var rafId = null;

    function positionLabel() {
      if (!active || !floatLabel || !stage) return;
      var hsRect = active.getBoundingClientRect();
      var stageRect = stage.getBoundingClientRect();
      floatLabel.style.left = (hsRect.left + hsRect.width / 2 - stageRect.left) + 'px';
      floatLabel.style.top = (hsRect.top - stageRect.top - 10) + 'px';
      rafId = requestAnimationFrame(positionLabel);
    }

    function showLabel(hs) {
      if (!floatLabel) return;
      var labelEl = hs.querySelector('.stamp-hotspot__label');
      floatLabel.textContent = labelEl ? labelEl.textContent : '';
      floatLabel.classList.add('is-visible');
      if (rafId) cancelAnimationFrame(rafId);
      positionLabel();
    }

    function hideLabel() {
      if (!floatLabel) return;
      floatLabel.classList.remove('is-visible');
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function release() {
      if (active) return; // another product took over before this frame
      root.classList.remove('is-zooming');
      root.classList.remove('is-hovering');
      hideLabel();
    }

    function activate(hs) {
      active = hs;
      root.style.setProperty('--zoom-x', (parseFloat(hs.style.left) || 50) + '%');
      root.style.setProperty('--zoom-y', (parseFloat(hs.style.top) || 50) + '%');
      root.classList.add('is-hovering');
      root.classList.add('is-zooming');
      showLabel(hs);
    }

    function deactivate(hs) {
      if (active !== hs) return;
      active = null;
      // Moving straight from one product to the next fires leave before the
      // neighbour's enter, so defer a frame rather than flashing the scrim off.
      requestAnimationFrame(release);
    }

    root.querySelectorAll('.stamp-hotspot').forEach(function (hs) {
      hs.addEventListener('pointerenter', function () { activate(hs); });
      hs.addEventListener('pointerleave', function () { deactivate(hs); });
      hs.addEventListener('focus', function () { activate(hs); });
      hs.addEventListener('blur', function () { deactivate(hs); });
    });
  }

  function initHero(root) {
    if (!root || root.dataset.shInit === 'true') return;
    root.dataset.shInit = 'true';

    initHover(root);

    if (!gsapOk()) return;

    var img = root.querySelector('.stamp-hero__img');
    var editorial = root.querySelector('[data-editorial]');
    if (img) window.gsap.from(img, { opacity: 0, duration: 0.7, ease: 'power2.out' });
    if (editorial) {
      window.gsap.from(editorial.children, {
        opacity: 0,
        y: 18,
        duration: 0.6,
        stagger: 0.08,
        delay: 0.12,
        ease: 'power3.out'
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-stamp-hero]').forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-stamp-hero]') : null;
    if (root) initHero(root);
  });
})();
